import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardBody,
  Badge,
  Spinner,
} from '@/components/ui';
import { transactionApi, getErrorMessage } from '@/lib/api';
import type { TransactionStatus, PaymentMethod } from '@/types';

interface Refund {
  id: string;
  refundRef: string;
  pspReference?: string;
  amount: number;
  currency: string;
  status: TransactionStatus;
  reason?: string;
  createdAt: string;
  processedAt?: string;
}

interface WebhookEvent {
  id: string;
  eventCode: string;
  eventDate: string;
  merchantReference?: string;
  pspReference?: string;
  processed: boolean;
  processedAt?: string;
  processingError?: string;
  rawPayload: Record<string, unknown>;
  createdAt: string;
}

interface TransactionDetail {
  id: string;
  transactionRef: string;
  pspReference?: string;
  merchantReference?: string;
  paymentLinkId: string;
  amount: number;
  currency: string;
  processingFee?: number;
  netAmount?: number;
  status: TransactionStatus;
  resultCode?: string;
  refusalReason?: string;
  paymentMethod?: PaymentMethod;
  cardBrand?: string;
  cardLastFour?: string;
  cardExpiryMonth?: string;
  cardExpiryYear?: string;
  cardHolderName?: string;
  threeDS2Result?: string;
  payerEmail?: string;
  payerName?: string;
  payerIp?: string;
  payerUserAgent?: string;
  refundedAmount: number;
  refundReason?: string;
  rawResponse?: Record<string, unknown>;
  createdAt: string;
  authorizedAt?: string;
  capturedAt?: string;
  settledAt?: string;
  refundedAt?: string;
  paymentLink: {
    id: string;
    linkCode: string;
    title: string;
    description?: string;
    paymentType: string;
    customerName?: string;
    customerEmail?: string;
    propertyLocation?: string;
    unitNumber?: string;
  };
  refunds: Refund[];
  webhookEvents: WebhookEvent[];
}

export function PaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    lifecycle: true,
    events: true,
    cardDetails: true,
    processing: true,
    refunds: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['transaction', id],
    queryFn: async () => {
      const response = await transactionApi.getById(id!);
      return response.data.data as TransactionDetail;
    },
    enabled: !!id,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatCurrency = (amount: number | string, currency = 'PHP') => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(numAmount);
  };

  const getStatusConfig = (status: TransactionStatus) => {
    const configs: Record<TransactionStatus, { color: string; bgColor: string; icon: JSX.Element; label: string }> = {
      AUTHORIZED: {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: <CheckCircleIcon className="w-5 h-5" />,
        label: 'Authorised',
      },
      CAPTURED: {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: <CheckCircleIcon className="w-5 h-5" />,
        label: 'Captured',
      },
      SETTLED: {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: <CheckCircleIcon className="w-5 h-5" />,
        label: 'Settled',
      },
      PENDING: {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: <ClockIcon className="w-5 h-5" />,
        label: 'Pending',
      },
      FAILED: {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: <XCircleIcon className="w-5 h-5" />,
        label: 'Failed',
      },
      CANCELLED: {
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        icon: <XCircleIcon className="w-5 h-5" />,
        label: 'Cancelled',
      },
      REFUNDED: {
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        icon: <ArrowPathIcon className="w-5 h-5" />,
        label: 'Refunded',
      },
      PARTIALLY_REFUNDED: {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: <ArrowPathIcon className="w-5 h-5" />,
        label: 'Partially Refunded',
      },
      CHARGEBACK: {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: <XCircleIcon className="w-5 h-5" />,
        label: 'Chargeback',
      },
    };
    return configs[status] || configs.PENDING;
  };

  const getPaymentMethodIcon = (method?: PaymentMethod, brand?: string) => {
    if (method === 'CARD') {
      const brandLower = brand?.toLowerCase() || '';
      if (brandLower.includes('visa')) {
        return (
          <span className="inline-flex items-center justify-center w-10 h-6 bg-white border border-gray-200 rounded text-xs font-bold text-blue-600">
            VISA
          </span>
        );
      }
      if (brandLower.includes('master')) {
        return (
          <span className="inline-flex items-center justify-center w-10 h-6 bg-white border border-gray-200 rounded text-xs font-bold text-orange-600">
            MC
          </span>
        );
      }
      if (brandLower.includes('amex')) {
        return (
          <span className="inline-flex items-center justify-center w-10 h-6 bg-white border border-gray-200 rounded text-xs font-bold text-blue-800">
            AMEX
          </span>
        );
      }
    }
    return null;
  };

  const buildPaymentLifecycle = (tx: TransactionDetail) => {
    const events: Array<{ type: string; date: string; user?: string }> = [];

    if (tx.authorizedAt) {
      events.push({
        type: 'Authorised',
        date: tx.authorizedAt,
        user: tx.payerEmail || 'System',
      });
    }

    if (tx.capturedAt) {
      events.push({
        type: 'Captured',
        date: tx.capturedAt,
        user: tx.payerEmail || 'System',
      });
    }

    if (tx.settledAt) {
      events.push({
        type: 'Settled',
        date: tx.settledAt,
        user: 'System',
      });
    }

    if (tx.refundedAt) {
      events.push({
        type: 'Refunded',
        date: tx.refundedAt,
        user: 'System',
      });
    }

    // If no specific dates, use createdAt with the current status
    if (events.length === 0) {
      events.push({
        type: tx.status === 'AUTHORIZED' ? 'Authorised' : tx.status === 'PENDING' ? 'Received' : tx.status,
        date: tx.createdAt,
        user: tx.payerEmail || 'System',
      });
    }

    // Add "Received" event at the beginning if there are authorization events
    if (tx.authorizedAt && events[0]?.type === 'Authorised') {
      events.unshift({
        type: 'Received',
        date: tx.createdAt,
        user: tx.payerEmail || 'System',
      });
    }

    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-error">{error ? getErrorMessage(error) : 'Transaction not found'}</p>
        <Button variant="outline" onClick={() => navigate('/admin/transactions')}>
          Back to Transactions
        </Button>
      </div>
    );
  }

  const tx = data;
  const statusConfig = getStatusConfig(tx.status);
  const lifecycle = buildPaymentLifecycle(tx);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/transactions')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment details</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline">See all payment actions</Button>
          <Button variant="outline" disabled>
            Refund payment
          </Button>
        </div>
      </div>

      {/* Status and Amount Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardBody className="p-6">
            <p className="text-sm text-gray-500 mb-2">Status</p>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${tx.status === 'AUTHORIZED' || tx.status === 'CAPTURED' || tx.status === 'SETTLED' ? 'bg-green-500' : tx.status === 'PENDING' ? 'bg-yellow-500' : 'bg-red-500'}`} />
              <a href="#" className="text-primary hover:underline font-medium">
                {statusConfig.label}
              </a>
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-400" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <p className="text-sm text-gray-500 mb-2">Amount</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(tx.amount, tx.currency)}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* PSP Reference Card */}
      <Card>
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Adyen PSP reference</p>
              <p className="font-mono text-gray-900">{tx.pspReference || tx.transactionRef}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="gray">Summary</Badge>
              <button
                onClick={() => copyToClipboard(tx.pspReference || tx.transactionRef, 'PSP Reference')}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Payment Lifecycle */}
      <Card>
        <CardBody className="p-0">
          <button
            onClick={() => toggleSection('lifecycle')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <h3 className="font-semibold text-gray-900">Payment lifecycle</h3>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                Last status: <span className="text-primary">{statusConfig.label}</span>
              </span>
              {expandedSections.lifecycle ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </button>

          {expandedSections.lifecycle && (
            <div className="px-6 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                      <th className="pb-3 font-medium">Journal type</th>
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Audit user</th>
                      <th className="pb-3 font-medium">Installment sequence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lifecycle.map((event, index) => (
                      <tr key={index}>
                        <td className="py-3 text-sm text-gray-900">{event.type}</td>
                        <td className="py-3 text-sm text-gray-500">
                          {format(new Date(event.date), 'MMM d, yyyy, h:mm:ss a')} GMT+8
                        </td>
                        <td className="py-3 text-sm text-gray-500">{event.user}</td>
                        <td className="py-3 text-sm text-gray-400">-</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-3">
                <button
                  onClick={() => copyToClipboard(JSON.stringify(lifecycle, null, 2), 'Payment lifecycle')}
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  Copy to clipboard
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Payment Events */}
      <Card>
        <CardBody className="p-0">
          <button
            onClick={() => toggleSection('events')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <h3 className="font-semibold text-gray-900">Payment events</h3>
            {expandedSections.events ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.events && (
            <div className="px-6 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium">PSP reference</th>
                      <th className="pb-3 font-medium">Merchant reference</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Amount</th>
                      <th className="pb-3 font-medium">Creation date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {/* Main payment event */}
                    <tr>
                      <td className="py-3 text-sm text-gray-900">Payment</td>
                      <td className="py-3 text-sm font-mono text-gray-900">
                        {tx.pspReference || tx.transactionRef}
                      </td>
                      <td className="py-3 text-sm font-mono text-gray-500">
                        {tx.merchantReference || tx.paymentLink.linkCode}
                      </td>
                      <td className="py-3 text-sm text-gray-900">{statusConfig.label}</td>
                      <td className="py-3 text-sm text-gray-900">
                        {formatCurrency(tx.amount, tx.currency)}
                      </td>
                      <td className="py-3 text-sm text-gray-500">
                        {format(new Date(tx.createdAt), 'MMM d, yyyy, h:mm:ss a')} GMT+8
                      </td>
                    </tr>
                    {/* Webhook events */}
                    {tx.webhookEvents.map((event) => (
                      <tr key={event.id}>
                        <td className="py-3 text-sm text-gray-900">{event.eventCode}</td>
                        <td className="py-3 text-sm font-mono text-gray-900">
                          {event.pspReference || '-'}
                        </td>
                        <td className="py-3 text-sm font-mono text-gray-500">
                          {event.merchantReference || '-'}
                        </td>
                        <td className="py-3 text-sm text-gray-900">
                          {event.processed ? 'Processed' : 'Pending'}
                        </td>
                        <td className="py-3 text-sm text-gray-500">-</td>
                        <td className="py-3 text-sm text-gray-500">
                          {format(new Date(event.eventDate), 'MMM d, yyyy, h:mm:ss a')} GMT+8
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-3">
                <button
                  onClick={() =>
                    copyToClipboard(
                      JSON.stringify([tx, ...tx.webhookEvents], null, 2),
                      'Payment events'
                    )
                  }
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  Copy to clipboard
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Card Details and Processing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card Details */}
        <Card>
          <CardBody className="p-0">
            <button
              onClick={() => toggleSection('cardDetails')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <h3 className="font-semibold text-gray-900">Card details</h3>
              {expandedSections.cardDetails ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.cardDetails && (
              <div className="px-6 pb-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Payment method</span>
                  <div className="flex items-center gap-2">
                    {getPaymentMethodIcon(tx.paymentMethod, tx.cardBrand)}
                    <span className="text-sm text-gray-900">{tx.cardBrand || tx.paymentMethod || '-'}</span>
                  </div>
                </div>
                {tx.cardBrand && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Card type</span>
                    <span className="text-sm text-gray-900">{tx.cardBrand}</span>
                  </div>
                )}
                {tx.cardLastFour && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Card number</span>
                    <span className="text-sm font-mono text-gray-900">•••• •••• •••• {tx.cardLastFour}</span>
                  </div>
                )}
                {tx.cardExpiryMonth && tx.cardExpiryYear && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Expiry</span>
                    <span className="text-sm text-gray-900">
                      {tx.cardExpiryMonth}/{tx.cardExpiryYear}
                    </span>
                  </div>
                )}
                {tx.cardHolderName && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Cardholder name</span>
                    <span className="text-sm text-gray-900">{tx.cardHolderName}</span>
                  </div>
                )}
                {tx.threeDS2Result && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">3D Secure</span>
                    <span className="text-sm text-gray-900">{tx.threeDS2Result}</span>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Processing */}
        <Card>
          <CardBody className="p-0">
            <button
              onClick={() => toggleSection('processing')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <h3 className="font-semibold text-gray-900">Processing</h3>
              {expandedSections.processing ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.processing && (
              <div className="px-6 pb-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Acquirer response</span>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${tx.resultCode === 'Authorised' ? 'bg-green-500' : tx.resultCode === 'Refused' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                    <a href="#" className="text-sm text-primary hover:underline">
                      {tx.resultCode === 'Authorised' ? 'Approved (AUTHORISED)' : tx.resultCode || 'Pending'}
                    </a>
                  </div>
                </div>
                {tx.resultCode && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Result code</span>
                    <span className="text-sm text-gray-900">{tx.resultCode}</span>
                  </div>
                )}
                {tx.refusalReason && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Refusal reason</span>
                    <span className="text-sm text-red-600">{tx.refusalReason}</span>
                  </div>
                )}
                {tx.processingFee && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Processing fee</span>
                    <span className="text-sm text-gray-900">
                      {formatCurrency(tx.processingFee, tx.currency)}
                    </span>
                  </div>
                )}
                {tx.netAmount && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Net amount</span>
                    <span className="text-sm text-gray-900">
                      {formatCurrency(tx.netAmount, tx.currency)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Payer Information */}
      <Card>
        <CardBody className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Payer information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Name</p>
              <p className="text-sm text-gray-900">
                {tx.payerName || tx.cardHolderName || tx.paymentLink.customerName || '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Email</p>
              <p className="text-sm text-gray-900">
                {tx.payerEmail || tx.paymentLink.customerEmail || '-'}
              </p>
            </div>
            {tx.payerIp && (
              <div>
                <p className="text-sm text-gray-500 mb-1">IP Address</p>
                <p className="text-sm font-mono text-gray-900">{tx.payerIp}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Payment Link Reference */}
      <Card>
        <CardBody className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Payment link reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Title</p>
              <p className="text-sm text-gray-900">{tx.paymentLink.title}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Link code</p>
              <p className="text-sm font-mono text-gray-900">{tx.paymentLink.linkCode}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Payment type</p>
              <p className="text-sm text-gray-900">
                {tx.paymentLink.paymentType.replace('_', ' ')}
              </p>
            </div>
            {tx.paymentLink.propertyLocation && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Property location</p>
                <p className="text-sm text-gray-900">{tx.paymentLink.propertyLocation}</p>
              </div>
            )}
            {tx.paymentLink.unitNumber && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Unit number</p>
                <p className="text-sm text-gray-900">{tx.paymentLink.unitNumber}</p>
              </div>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <Link
              to={`/admin/payment-links/${tx.paymentLinkId}`}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              View payment link details
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </Link>
          </div>
        </CardBody>
      </Card>

      {/* Refunds (if any) */}
      {tx.refunds.length > 0 && (
        <Card>
          <CardBody className="p-0">
            <button
              onClick={() => toggleSection('refunds')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <h3 className="font-semibold text-gray-900">Refunds ({tx.refunds.length})</h3>
              {expandedSections.refunds ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.refunds && (
              <div className="px-6 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                        <th className="pb-3 font-medium">Refund ref</th>
                        <th className="pb-3 font-medium">PSP reference</th>
                        <th className="pb-3 font-medium">Amount</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Reason</th>
                        <th className="pb-3 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tx.refunds.map((refund) => (
                        <tr key={refund.id}>
                          <td className="py-3 text-sm font-mono text-gray-900">
                            {refund.refundRef}
                          </td>
                          <td className="py-3 text-sm font-mono text-gray-500">
                            {refund.pspReference || '-'}
                          </td>
                          <td className="py-3 text-sm text-gray-900">
                            {formatCurrency(refund.amount, refund.currency)}
                          </td>
                          <td className="py-3">
                            <Badge
                              variant={
                                refund.status === 'REFUNDED'
                                  ? 'success'
                                  : refund.status === 'PENDING'
                                  ? 'warning'
                                  : 'error'
                              }
                            >
                              {refund.status}
                            </Badge>
                          </td>
                          <td className="py-3 text-sm text-gray-500">{refund.reason || '-'}</td>
                          <td className="py-3 text-sm text-gray-500">
                            {format(new Date(refund.createdAt), 'MMM d, yyyy h:mm a')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Refunded Amount Summary */}
      {tx.refundedAmount > 0 && (
        <Card>
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total refunded</p>
                <p className="text-xl font-bold text-red-600">
                  -{formatCurrency(tx.refundedAmount, tx.currency)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Net amount</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(Number(tx.amount) - Number(tx.refundedAmount), tx.currency)}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
